import { BaseElementDefBase, BaseElementDef, isILockableElement, LockAction } from "./BaseElementDef";
import { BehaviorSubject, combineLatest } from "rxjs";
import { StateFormSettings } from "@cs-ts/state-form-settings";
import { FormAccessPurpose } from "@cs/FormAccessPurpose";
import { FormFormStateItem } from "@cs/FormFormStateItem";
import { CalculationContext } from "./__CalculationContextInterface";
import { FormDefinitionItem } from "./__DefinitionDataInterface";
import { map } from "rxjs/operators";

type allFormAccessPurposes = (typeof FormAccessPurpose)["ALL"][0];

export default class StateFormDef extends BaseElementDef<StateFormSettings, FormDefinitionItem<StateFormSettings>, FormFormStateItem> {
    declare _status$: BehaviorSubject<string>;
    declare status: string;
    declare state: string;
    declare activatedDate: string;
    declare completedDate: string;
    declare locks: string[];
    declare _can: Record<allFormAccessPurposes, boolean>;
    declare isAssignedToOtherUser: boolean;

    constructor(parentElement: BaseElementDefBase | null, elementData: FormFormStateItem, definitionData: FormDefinitionItem<StateFormSettings>, calculationContext: CalculationContext) {
        super(parentElement, elementData, definitionData, calculationContext);
        this.typeName = "MI.Common.DynamicForms.Models.DFStateForm"; // TODO - not hardcoded?
        this._status$ = this._status$ || new BehaviorSubject(null);
    }

    can(type: "view" | "assign" | "edit" | "unlock", asReviewer: boolean) {
        const fullType = type == "view" ? (asReviewer ? FormAccessPurpose.AdminView : FormAccessPurpose.UserView) : type == "assign" ? (asReviewer ? FormAccessPurpose.AdminWorkflowAssign : FormAccessPurpose.UserAssign) : type == "edit" ? (asReviewer ? FormAccessPurpose.AdminWorkflowEdit : FormAccessPurpose.UserEdit) : type == "unlock" ? (asReviewer ? FormAccessPurpose.AdminWorkflowUnlock : FormAccessPurpose.UserUnlock) : null!;
        return this._can[fullType];
    }

    getData(excludeChildren?: boolean) {
        const result = super.getData(excludeChildren) as FormFormStateItem;
        if (result != null) {
            result.status = this.status;
        }
        return result;
    }

    getLockActions(lock: string): LockAction[] {
        const lockEls = this.getElementsByFilter2(isILockableElement).filter(i => i != this);
        const r = lockEls.flatMap(i => i.getLockActions(lock));
        return r;
    }

    setData(data: FormFormStateItem, excludeChildren: boolean) {
        super.setData(data, excludeChildren);
        this._can = (data.can ?? {}) as Record<allFormAccessPurposes, boolean>;
        this.activatedDate = data.activatedDate as string;
        this.completedDate = data.completedDate as string;
        this.isAssignedToOtherUser = data.isAssignedToOtherUser as boolean;
        this.locks = data.locks;
        this.setStatus(data.status as string);
    }

    setStatus(status: string) {
        this.status = status;
        if (this._status$ == null) {
            this._status$ = new BehaviorSubject(status);
        } else {
            this._status$.next(status);
        }
    }

    clientStatus$() {
        return combineLatest(this._status$, this.valid$()).pipe(map(([status, valid]) => (status == null ? (valid ? "Complete" : "Invalid") : status)));
    }
}
